/**
 * Shopping Catalogue - STANDALONE SHOPPING LIST TAXONOMY
 * 
 * This catalogue is COMPLETELY SEPARATE from the budget/expense category system.
 * It provides a unified household + African grocery taxonomy for the Shopping List.
 * 
 * NOT connected to: envelopes, budgets, expense categories, or financial tracking.
 * ONLY used for: organising shopping list items by product category.
 */

export const SHOPPING_CATALOGUE = {
  // ══════════════════════════════════════════════════════════════════════════
  // 1. FRESH PRODUCE, VEGETABLES & TUBERS
  // ══════════════════════════════════════════════════════════════════════════
  'fresh-produce': {
    id: 'fresh-produce',
    name: 'Fresh Produce, Vegetables & Tubers',
    emoji: '🥬',
    subcategories: [
      {
        id: 'fruits',
        name: 'Fruits',
        items: [
          'Apples', 'Apple', 'Bananas', 'Banana', 'Oranges', 'Orange', 'Grapes', 'Grape', 
          'Strawberries', 'Strawberry', 'Blueberries', 'Blueberry', 'Raspberries', 'Raspberry',
          'Lemons', 'Lemon', 'Limes', 'Lime', 'Avocados', 'Avocado', 'Mangoes', 'Mango',
          'Pineapple', 'Watermelon', 'Cantaloupe', 'Honeydew', 'Kiwi', 'Peaches', 'Peach',
          'Pears', 'Pear', 'Plums', 'Plum', 'Cherries', 'Cherry', 'Pomegranate',
          'Plantains (Green)', 'Plantains (Ripe)', 'Plantain', 'Pawpaw (Papaya)', 'Papaya',
          'Coconut', 'Passion Fruit', 'Guava', 'Soursop', 'African Star Apple (Agbalumo)'
        ]
      },
      {
        id: 'vegetables-leafy-greens',
        name: 'Vegetables & Leafy Greens',
        items: [
          'Lettuce', 'Spinach', 'Kale', 'Tomatoes', 'Tomato', 'Cherry Tomatoes',
          'Cucumbers', 'Cucumber', 'Bell Peppers', 'Bell Pepper', 'Red Pepper', 'Green Pepper',
          'Carrots', 'Carrot', 'Broccoli', 'Cauliflower', 'Cabbage', 'Red Cabbage',
          'Zucchini', 'Courgette', 'Mushrooms', 'Mushroom', 'Celery', 'Asparagus',
          'Green Beans', 'Peas', 'Sweetcorn', 'Corn on the Cob', 'Aubergine', 'Eggplant',
          'Leeks', 'Spring Onions', 'Radishes', 'Beetroot', 'Parsnips', 'Turnips', 'Swede',
          'Okra', 'Garden Eggs (African Eggplant)', 'Ugu', 'Ewedu', 'Bitterleaf', 
          'Okazi', 'Sukuma Wiki', 'Callaloo', 'Water Leaf', 'Scent Leaf (Efirin)',
          'Curry Leaves', 'Bay Leaves', 'Coriander', 'Mint', 'Basil', 'Parsley', 'Thyme', 'Rosemary'
        ]
      },
      {
        id: 'tubers-roots',
        name: 'Tubers & Roots',
        items: [
          'Potatoes', 'Potato', 'Sweet Potatoes', 'Sweet Potato', 'New Potatoes',
          'Baking Potatoes', 'Mashed Potato', 'Roasting Potatoes',
          'Yam (Puna)', 'Yam (White)', 'Yam', 'Cassava (Yuca)', 'Cassava',
          'Cocoyam (Taro / Ede)', 'Cocoyam', 'Taro', 'Eddoes'
        ]
      },
      {
        id: 'aromatics-peppers',
        name: 'Aromatics & Peppers',
        items: [
          'Onions', 'Onion', 'Red Onions', 'White Onions', 'Garlic', 'Ginger',
          'Scallions', 'Shallots', 'Chives', 'Lemongrass',
          'Scotch Bonnet Peppers (Rodo)', 'Scotch Bonnet', 'Bird\'s Eye Chillies',
          'Jalapeños', 'Habanero', 'Red Chillies', 'Green Chillies', 'Dried Chillies'
        ]
      }
    ]
  },

  // ══════════════════════════════════════════════════════════════════════════
  // 2. MEAT, POULTRY & SEAFOOD
  // ══════════════════════════════════════════════════════════════════════════
  'meat-poultry-seafood': {
    id: 'meat-poultry-seafood',
    name: 'Meat, Poultry & Seafood',
    emoji: '🥩',
    subcategories: [
      {
        id: 'poultry',
        name: 'Poultry',
        items: [
          'Chicken', 'Chicken Breasts', 'Chicken Thighs', 'Chicken Wings', 'Chicken Drumsticks',
          'Whole Chicken', 'Chicken Mince', 'Turkey', 'Turkey Breast', 'Turkey Mince',
          'Duck', 'Cornish Hen', 'Chicken Liver', 'Chicken Gizzard'
        ]
      },
      {
        id: 'red-meat-assorted',
        name: 'Red Meat & Assorted Meats',
        items: [
          'Beef', 'Beef Mince', 'Beef (Ground)', 'Beef (Steak)', 'Steak', 'Sirloin Steak',
          'Ribeye Steak', 'Fillet Steak', 'Beef Brisket', 'Beef Ribs', 'Roasting Beef',
          'Pork', 'Pork Chops', 'Pork Loin', 'Pork Belly', 'Pork Mince', 'Pork Ribs',
          'Bacon', 'Streaky Bacon', 'Back Bacon', 'Lamb', 'Lamb Chops', 'Lamb Leg',
          'Lamb Mince', 'Lamb Shoulder', 'Goat Meat', 'Goat', 'Venison', 'Veal',
          'Cow Tail', 'Oxtail', 'Shaki (Tripe)', 'Tripe', 'Cow Foot', 'Ponmo (Cowhide)',
          'Offal', 'Kidney', 'Liver', 'Heart'
        ]
      },
      {
        id: 'processed-deli',
        name: 'Processed / Deli',
        items: [
          'Sausages', 'Pork Sausages', 'Beef Sausages', 'Cumberland Sausages', 'Chipolatas',
          'Hot Dogs', 'Ham', 'Smoked Ham', 'Parma Ham', 'Serrano Ham',
          'Turkey (Lunch Meat)', 'Salami', 'Chorizo', 'Pepperoni', 'Prosciutto',
          'Corned Beef', 'Spam', 'Pâté', 'Scotch Eggs', 'Pork Pies', 'Suya'
        ]
      },
      {
        id: 'fresh-frozen-seafood',
        name: 'Fresh & Frozen Seafood',
        items: [
          'Salmon', 'Salmon Fillets', 'Smoked Salmon', 'Tuna', 'Tuna Steak',
          'Shrimp', 'Prawns', 'King Prawns', 'Tiger Prawns', 'Scampi',
          'Cod', 'Cod Fillets', 'Tilapia', 'Tilapia Fillets', 'Haddock', 'Mackerel',
          'Sea Bass', 'Trout', 'Sardines', 'Anchovies', 'Fish Fingers',
          'Calamari', 'Squid', 'Mussels', 'Clams', 'Oysters', 'Crab', 'Lobster',
          'Fish Cakes', 'Breaded Fish', 'Battered Fish'
        ]
      },
      {
        id: 'dried-smoked-seafood',
        name: 'Dried & Smoked Seafood',
        items: [
          'Stockfish (Dried Cod)', 'Stockfish', 'Smoked Catfish', 'Dried Catfish',
          'Dried Fish', 'Smoked Fish', 'Dried Prawns', 'Dried Shrimp', 'Crayfish (Dried)',
          'Canned Tuna', 'Canned Salmon', 'Canned Sardines', 'Canned Mackerel'
        ]
      }
    ]
  },

  // ══════════════════════════════════════════════════════════════════════════
  // 3. DAIRY & DAIRY ALTERNATIVES
  // ══════════════════════════════════════════════════════════════════════════
  'dairy': {
    id: 'dairy',
    name: 'Dairy & Dairy Alternatives',
    emoji: '🥛',
    subcategories: [
      {
        id: 'milk-cream',
        name: 'Milk & Cream',
        items: [
          'Milk', 'Whole Milk', 'Semi-Skimmed Milk', 'Skimmed Milk', 'Full Fat Milk',
          'Heavy Cream', 'Double Cream', 'Single Cream', 'Whipping Cream',
          'Half-and-Half', 'Clotted Cream', 'Evaporated Milk', 'Condensed Milk'
        ]
      },
      {
        id: 'cheese',
        name: 'Cheese',
        items: [
          'Cheese', 'Cheddar', 'Cheddar Cheese', 'Mature Cheddar', 'Mild Cheddar',
          'Mozzarella', 'Parmesan', 'Grated Parmesan', 'Cottage Cheese', 'Cream Cheese',
          'Philadelphia', 'Brie', 'Camembert', 'Feta', 'Gouda', 'Emmental', 'Gruyère',
          'Red Leicester', 'Stilton', 'Blue Cheese', 'Halloumi', 'Goat Cheese',
          'Cheese Slices', 'Cheese Strings', 'Babybel', 'Laughing Cow', 'Dairylea'
        ]
      },
      {
        id: 'cultured-dairy',
        name: 'Cultured Dairy & Eggs',
        items: [
          'Yogurt', 'Greek Yogurt', 'Yogurt (Greek)', 'Yogurt (Plain)', 'Natural Yogurt',
          'Flavoured Yogurt', 'Fruit Yogurt', 'Müller Corner', 'Activia',
          'Sour Cream', 'Crème Fraîche', 'Butter', 'Salted Butter', 'Unsalted Butter',
          'Spreadable Butter', 'Lurpak', 'Margarine', 'Flora', 'Stork',
          'Eggs', 'Free Range Eggs', 'Organic Eggs', 'Large Eggs', 'Medium Eggs'
        ]
      },
      {
        id: 'plant-based',
        name: 'Plant-Based Alternatives',
        items: [
          'Almond Milk', 'Soy Milk', 'Soya Milk', 'Oat Milk', 'Oatly', 'Coconut Milk',
          'Rice Milk', 'Cashew Milk', 'Hemp Milk', 'Plant-Based Butter', 'Vegan Butter',
          'Vegan Cheese', 'Dairy-Free Yogurt', 'Coconut Yogurt', 'Alpro'
        ]
      }
    ]
  },

  // ══════════════════════════════════════════════════════════════════════════
  // 4. GRAINS, RICE, FLOURS & FUFU BASES
  // ══════════════════════════════════════════════════════════════════════════
  'grains-rice-flour': {
    id: 'grains-rice-flour',
    name: 'Grains, Rice, Flours & Fufu Bases',
    emoji: '🍚',
    subcategories: [
      {
        id: 'bread-wraps',
        name: 'Bread & Wraps',
        items: [
          'Bread', 'White Bread', 'Brown Bread', 'Wholemeal Bread', 'Sandwich Bread',
          'Sliced Bread', 'Hovis', 'Warburtons', 'Kingsmill', 'Sourdough', 'Ciabatta',
          'Baguette', 'French Bread', 'Rolls', 'Bread Rolls', 'Burger Buns', 'Hot Dog Buns',
          'Bagels', 'English Muffins', 'Crumpets', 'Pitta Bread', 'Naan Bread', 'Chapati',
          'Tortillas', 'Wraps', 'Flour Tortillas', 'Corn Tortillas', 'Flatbread',
          'Brioche', 'Croissants', 'Pain au Chocolat', 'Danish Pastries'
        ]
      },
      {
        id: 'rice-grains',
        name: 'Rice & Grains',
        items: [
          'Rice', 'White Rice', 'Brown Rice', 'Basmati Rice', 'Long Grain Rice',
          'Jasmine Rice', 'Risotto Rice', 'Arborio Rice', 'Wild Rice', 'Sushi Rice',
          'Jollof Rice Varieties', 'Ofada Rice', 'Uncle Bens', 'Tilda',
          'Pasta', 'Spaghetti', 'Penne', 'Fusilli', 'Tagliatelle', 'Linguine',
          'Macaroni', 'Lasagne Sheets', 'Cannelloni', 'Orzo', 'Couscous',
          'Quinoa', 'Bulgur Wheat', 'Pearl Barley', 'Oats', 'Porridge Oats',
          'Rolled Oats', 'Steel Cut Oats', 'Instant Oats', 'Teff', 'Millet', 'Semolina'
        ]
      },
      {
        id: 'flours-fufu',
        name: 'Flours & Fufu Bases',
        items: [
          'Flour', 'Plain Flour', 'Self-Raising Flour', 'All-Purpose Flour', 'Wheat Flour',
          'Strong Bread Flour', 'Wholemeal Flour', 'Cornflour', 'Corn Starch', 'Rice Flour',
          'Gram Flour', 'Chickpea Flour', 'Almond Flour', 'Coconut Flour',
          'Pancake Mix', 'Cake Mix', 'Brownie Mix',
          'Garri', 'Garri (White)', 'Garri (Yellow)', 'Garri (Ijebu)',
          'Pounded Yam Flour', 'Poundo Yam', 'Fufu Flour', 'Fufu Flour (Cassava)', 
          'Fufu Flour (Plantain)', 'Fufu Flour (Cocoyam)', 'Amala Flour', 'Elubo',
          'Semovita', 'Wheat Swallow', 'Eba'
        ]
      },
      {
        id: 'legumes-beans',
        name: 'Legumes & Beans',
        items: [
          'Beans', 'Kidney Beans', 'Red Kidney Beans', 'Black Beans', 'Butter Beans',
          'Cannellini Beans', 'Haricot Beans', 'Baked Beans', 'Heinz Baked Beans',
          'Chickpeas', 'Lentils', 'Red Lentils', 'Green Lentils', 'Puy Lentils',
          'Split Peas', 'Black-Eyed Peas', 'Black Eyed Beans', 'Honey Beans (Ewa Oloyin)',
          'Pigeon Peas', 'Broad Beans', 'Fava Beans', 'Edamame'
        ]
      },
      {
        id: 'breakfast-cereals',
        name: 'Breakfast Cereals',
        items: [
          'Cereal', 'Cornflakes', 'Weetabix', 'Shreddies', 'Cheerios', 'Special K',
          'Bran Flakes', 'Rice Krispies', 'Coco Pops', 'Frosties', 'Crunchy Nut',
          'Granola', 'Muesli', 'Porridge', 'Ready Brek', 'Shredded Wheat',
          'Fruit and Fibre', 'All-Bran'
        ]
      }
    ]
  },

  // ══════════════════════════════════════════════════════════════════════════
  // 5. PANTRY STAPLES, OILS & SEASONINGS
  // ══════════════════════════════════════════════════════════════════════════
  'pantry-staples': {
    id: 'pantry-staples',
    name: 'Pantry Staples, Oils & Seasonings',
    emoji: '🫒',
    subcategories: [
      {
        id: 'oils-vinegar',
        name: 'Oils & Vinegar',
        items: [
          'Oil', 'Olive Oil', 'Extra Virgin Olive Oil', 'Vegetable Oil', 'Sunflower Oil',
          'Rapeseed Oil', 'Coconut Oil', 'Sesame Oil', 'Groundnut Oil', 'Avocado Oil',
          'Palm Oil', 'Red Palm Oil', 'Cooking Spray', 'Fry Light',
          'Vinegar', 'Balsamic Vinegar', 'White Wine Vinegar', 'Red Wine Vinegar',
          'Apple Cider Vinegar', 'Malt Vinegar', 'Rice Vinegar'
        ]
      },
      {
        id: 'canned-jarred',
        name: 'Canned & Jarred Foods',
        items: [
          'Tomatoes', 'Chopped Tomatoes', 'Diced Tomatoes', 'Tinned Tomatoes', 'Tomato Purée',
          'Tomato Paste', 'Passata', 'Sun-Dried Tomatoes', 'Pasta Sauce', 'Dolmio',
          'Soup', 'Heinz Soup', 'Tomato Soup', 'Chicken Soup', 'Vegetable Soup',
          'Pickles', 'Gherkins', 'Olives', 'Capers', 'Artichokes',
          'Jam', 'Strawberry Jam', 'Marmalade', 'Honey', 'Golden Syrup', 'Maple Syrup',
          'Peanut Butter', 'Nutella', 'Chocolate Spread', 'Marmite', 'Bovril',
          'Groundnut Paste', 'Tahini', 'Hummus'
        ]
      },
      {
        id: 'spices-seasonings',
        name: 'Spices & Seasonings',
        items: [
          'Salt', 'Sea Salt', 'Table Salt', 'Rock Salt', 'Black Pepper', 'Pepper',
          'White Pepper', 'Paprika', 'Smoked Paprika', 'Cayenne Pepper', 'Chili Powder',
          'Oregano', 'Basil', 'Thyme', 'Rosemary', 'Sage', 'Bay Leaves', 'Parsley',
          'Cumin', 'Coriander', 'Turmeric', 'Garam Masala', 'Curry Powder', 'Cinnamon',
          'Nutmeg', 'Cloves', 'Cardamom', 'Allspice', 'Mixed Spice', 'Chinese Five Spice',
          'Garlic Powder', 'Onion Powder', 'Ginger Powder', 'Mustard Powder',
          'Maggi', 'Maggi Cubes', 'Knorr', 'Knorr Cubes', 'Knorr Aromat', 'Stock Cubes',
          'Chicken Stock', 'Beef Stock', 'Vegetable Stock', 'Oxo Cubes',
          'Suya Spice (Yaji)', 'Yaji', 'Berbere Spice Blend', 'Peri-Peri Seasoning',
          'Ground Crayfish', 'Crayfish', 'Jerk Seasoning', 'Cajun Seasoning'
        ]
      },
      {
        id: 'soup-thickeners',
        name: 'Soup Thickeners & Fermented Condiments',
        items: [
          'Egusi', 'Egusi Seeds', 'Ogbono', 'Ogbono Seeds', 'Achi Powder', 'Achi',
          'Ofor', 'Iru', 'Locust Beans', 'Ogiri', 'Dadawa', 'Dawadawa',
          'Uziza Seeds', 'Ehuru (Calabash Nutmeg)'
        ]
      },
      {
        id: 'baking-essentials',
        name: 'Baking Essentials',
        items: [
          'Sugar', 'White Sugar', 'Caster Sugar', 'Granulated Sugar', 'Brown Sugar',
          'Demerara Sugar', 'Icing Sugar', 'Muscovado Sugar', 'Sweetener', 'Stevia',
          'Baking Soda', 'Bicarbonate of Soda', 'Baking Powder', 'Yeast', 'Dried Yeast',
          'Vanilla Extract', 'Vanilla Essence', 'Almond Extract', 'Food Colouring',
          'Cocoa Powder', 'Chocolate Chips', 'Desiccated Coconut', 'Dried Fruit',
          'Raisins', 'Sultanas', 'Currants', 'Mixed Peel', 'Glace Cherries'
        ]
      },
      {
        id: 'condiments-sauces',
        name: 'Condiments & Sauces',
        items: [
          'Ketchup', 'Tomato Ketchup', 'Heinz Ketchup', 'Mustard', 'English Mustard',
          'Dijon Mustard', 'Wholegrain Mustard', 'Mayonnaise', 'Hellmanns', 'Salad Cream',
          'Soy Sauce', 'Dark Soy Sauce', 'Light Soy Sauce', 'Oyster Sauce', 'Fish Sauce',
          'Worcestershire Sauce', 'HP Sauce', 'Brown Sauce', 'BBQ Sauce', 'Teriyaki Sauce',
          'Hot Sauce', 'Tabasco', 'Sriracha', 'Franks Hot Sauce', 'Chilli Sauce',
          'Sweet Chilli Sauce', 'Hoisin Sauce', 'Pesto', 'Green Pesto', 'Red Pesto',
          'Horseradish', 'Mint Sauce', 'Cranberry Sauce', 'Gravy', 'Bisto'
        ]
      }
    ]
  },

  // ══════════════════════════════════════════════════════════════════════════
  // 6. FROZEN FOODS & BEVERAGES
  // ══════════════════════════════════════════════════════════════════════════
  'frozen-beverages': {
    id: 'frozen-beverages',
    name: 'Frozen Foods & Beverages',
    emoji: '🧊',
    subcategories: [
      {
        id: 'frozen-items',
        name: 'Frozen Items',
        items: [
          'Frozen Vegetables', 'Frozen Vegetables (Mixed)', 'Frozen Vegetables (Peas)',
          'Frozen Peas', 'Frozen Sweetcorn', 'Frozen Chips', 'Oven Chips', 'Frozen Chips McCain',
          'Frozen Fruits', 'Frozen Berries', 'Frozen Spinach',
          'Pizza', 'Frozen Pizza', 'Chicago Town', 'Dr Oetker', 'Goodfellas',
          'Ice Cream', 'Ben and Jerrys', 'Häagen-Dazs', 'Magnum', 'Cornetto',
          'Ice Lollies', 'Frozen Desserts', 'Cheesecake', 'Arctic Roll',
          'Frozen Meat', 'Frozen Chicken', 'Frozen Burgers', 'Beef Burgers',
          'Fish Fingers', 'Birds Eye', 'Frozen Fish', 'Breaded Chicken',
          'Chicken Nuggets', 'Chicken Dippers', 'Turkey Dinosaurs',
          'Frozen Pastry', 'Puff Pastry', 'Shortcrust Pastry', 'Filo Pastry',
          'Yorkshire Puddings', 'Roast Potatoes', 'Potato Waffles', 'Hash Browns',
          'Frozen Ready Meals', 'Ready Meals'
        ]
      },
      {
        id: 'non-alcoholic',
        name: 'Non-Alcoholic Beverages',
        items: [
          'Water', 'Bottled Water', 'Sparkling Water', 'Mineral Water', 'Evian', 'Volvic',
          'Coffee', 'Instant Coffee', 'Nescafe', 'Ground Coffee', 'Coffee Beans', 'Decaf Coffee',
          'Tea', 'Tea Bags', 'PG Tips', 'Tetley', 'Yorkshire Tea', 'Twinings',
          'Green Tea', 'Herbal Tea', 'Peppermint Tea', 'Camomile Tea', 'Fruit Tea',
          'Juice', 'Orange Juice', 'Apple Juice', 'Tropicana', 'Ribena', 'Fruit Juice',
          'Squash', 'Robinsons', 'Vimto', 'Cordial',
          'Soda', 'Coca Cola', 'Coke', 'Pepsi', 'Fanta', 'Sprite', '7UP', 'Lemonade',
          'Diet Coke', 'Coke Zero', 'Lucozade', 'Irn Bru', 'Ginger Beer', 'Tonic Water',
          'Energy Drinks', 'Red Bull', 'Monster', 'Sports Drinks', 'Powerade', 'Gatorade',
          'Hot Chocolate', 'Drinking Chocolate', 'Ovaltine', 'Horlicks', 'Milo'
        ]
      },
      {
        id: 'alcoholic',
        name: 'Alcoholic Beverages',
        items: [
          'Beer', 'Lager', 'Stella Artois', 'Budweiser', 'Heineken', 'Corona', 'Peroni',
          'Ale', 'Craft Beer', 'IPA', 'Stout', 'Guinness', 'Cider', 'Strongbow', 'Kopparberg',
          'Wine', 'Red Wine', 'White Wine', 'Rosé Wine', 'Prosecco', 'Champagne',
          'Spirits', 'Vodka', 'Gin', 'Rum', 'Whisky', 'Whiskey', 'Brandy', 'Tequila',
          'Baileys', 'Amaretto', 'Liqueur', 'Mixers'
        ]
      }
    ]
  },

  // ══════════════════════════════════════════════════════════════════════════
  // 7. HOUSEHOLD SUPPLIES & PERSONAL CARE
  // ══════════════════════════════════════════════════════════════════════════
  'household-personal': {
    id: 'household-personal',
    name: 'Household Supplies & Personal Care',
    emoji: '🧹',
    subcategories: [
      {
        id: 'cleaning-supplies',
        name: 'Cleaning Supplies',
        items: [
          'Dish Soap', 'Washing Up Liquid', 'Fairy Liquid', 'Dishwasher Tablets',
          'Finish Tablets', 'Rinse Aid', 'Laundry Detergent', 'Washing Powder',
          'Persil', 'Ariel', 'Bold', 'Daz', 'Surf', 'Fabric Conditioner', 'Lenor', 'Comfort',
          'Stain Remover', 'Vanish', 'Colour Catchers',
          'All-Purpose Cleaner', 'Flash', 'Dettol', 'Surface Cleaner', 'Kitchen Cleaner',
          'Bathroom Cleaner', 'Toilet Cleaner', 'Domestos', 'Harpic', 'Bleach',
          'Window Cleaner', 'Glass Cleaner', 'Mr Muscle', 'Oven Cleaner',
          'Floor Cleaner', 'Mop', 'Bucket', 'Broom', 'Dustpan', 'Vacuum Bags',
          'Sponges', 'Scourers', 'Cloths', 'Microfibre Cloths', 'J Cloths',
          'Rubber Gloves', 'Marigolds', 'Air Freshener', 'Febreze', 'Glade', 'Plug-In'
        ]
      },
      {
        id: 'paper-goods',
        name: 'Paper Goods',
        items: [
          'Paper Towels', 'Kitchen Roll', 'Plenty', 'Regina',
          'Toilet Paper', 'Toilet Roll', 'Andrex', 'Cushelle', 'Velvet',
          'Facial Tissues', 'Tissues', 'Kleenex', 'Kitchen Towel',
          'Napkins', 'Serviettes', 'Wet Wipes', 'Baby Wipes', 'Antibacterial Wipes'
        ]
      },
      {
        id: 'storage-misc',
        name: 'Storage & Miscellaneous',
        items: [
          'Bin Bags', 'Trash Bags', 'Bin Liners', 'Swing Bin Liners', 'Recycling Bags',
          'Aluminium Foil', 'Tin Foil', 'Cling Film', 'Plastic Wrap', 'Baking Parchment',
          'Sandwich Bags', 'Freezer Bags', 'Ziplock Bags', 'Food Bags',
          'Food Containers', 'Tupperware', 'Storage Boxes',
          'Batteries', 'AA Batteries', 'AAA Batteries', 'Duracell', 'Energizer',
          'Light Bulbs', 'LED Bulbs', 'Candles', 'Matches', 'Lighters',
          'Sellotape', 'Packing Tape', 'String', 'Scissors'
        ]
      },
      {
        id: 'personal-care-health',
        name: 'Personal Care & Health',
        items: [
          'Shampoo', 'Head and Shoulders', 'Pantene', 'TRESemmé', 'Herbal Essences',
          'Conditioner', 'Hair Mask', 'Hair Oil', 'Hair Gel', 'Hair Spray', 'Mousse',
          'Soap', 'Bar Soap', 'Hand Soap', 'Dove', 'Imperial Leather', 'Carex',
          'Body Wash', 'Shower Gel', 'Lynx', 'Nivea', 'Radox', 'Original Source',
          'Bubble Bath', 'Bath Bombs', 'Epsom Salts',
          'Toothpaste', 'Colgate', 'Sensodyne', 'Oral B', 'Toothbrush', 'Electric Toothbrush',
          'Mouthwash', 'Listerine', 'Dental Floss', 'Interdental Brushes',
          'Deodorant', 'Antiperspirant', 'Sure', 'Right Guard', 'Roll On', 'Spray',
          'Lotion', 'Body Lotion', 'Moisturiser', 'Face Cream', 'Hand Cream', 'E45',
          'Vaseline', 'Lip Balm', 'Chapstick', 'Sunscreen', 'Sun Cream', 'After Sun',
          'Razor', 'Razor Blades', 'Gillette', 'Venus', 'Shaving Foam', 'Shaving Gel',
          'Cotton Wool', 'Cotton Pads', 'Cotton Buds', 'Q-Tips', 'Nail Clippers',
          'Pain Relievers', 'Paracetamol', 'Ibuprofen', 'Aspirin', 'Nurofen',
          'Cold and Flu', 'Lemsip', 'Beechams', 'Strepsils', 'Cough Medicine',
          'Plasters', 'Band-Aids', 'Bandages', 'First Aid', 'Antiseptic Cream',
          'Vitamins', 'Vitamin C', 'Vitamin D', 'Multivitamins', 'Iron Tablets',
          'Hayfever Tablets', 'Antihistamines', 'Piriton', 'Allergy Relief',
          'Feminine Care Products', 'Sanitary Pads', 'Tampons', 'Always', 'Tampax',
          'Incontinence Pads', 'Tena'
        ]
      }
    ]
  },

  // ══════════════════════════════════════════════════════════════════════════
  // 8. SNACKS & CONFECTIONERY
  // ══════════════════════════════════════════════════════════════════════════
  'snacks-confectionery': {
    id: 'snacks-confectionery',
    name: 'Snacks & Confectionery',
    emoji: '🍫',
    subcategories: [
      {
        id: 'crisps-snacks',
        name: 'Crisps & Savoury Snacks',
        items: [
          'Crisps', 'Potato Chips', 'Walkers', 'Pringles', 'Doritos', 'Sensations',
          'Kettle Chips', 'Tyrells', 'McCoys', 'Hula Hoops', 'Wotsits', 'Quavers',
          'Monster Munch', 'Skips', 'Squares', 'French Fries', 'Nik Naks',
          'Popcorn', 'Butterkist', 'Propercorn', 'Sweet Popcorn', 'Salted Popcorn',
          'Nuts', 'Peanuts', 'Cashews', 'Almonds', 'Mixed Nuts', 'Dry Roasted Peanuts',
          'Bombay Mix', 'Pretzels', 'Breadsticks', 'Twiglets', 'Mini Cheddars',
          'Rice Cakes', 'Crackers', 'Cream Crackers', 'Ritz Crackers', 'Jacobs',
          'Oatcakes', 'Water Biscuits', 'Crispbreads'
        ]
      },
      {
        id: 'chocolate',
        name: 'Chocolate',
        items: [
          'Chocolate', 'Milk Chocolate', 'Dark Chocolate', 'White Chocolate',
          'Cadbury', 'Dairy Milk', 'Galaxy', 'Lindt', 'Milka', 'Toblerone',
          'Kit Kat', 'Twix', 'Mars Bar', 'Snickers', 'Bounty', 'Maltesers',
          'Minstrels', 'M&Ms', 'Revels', 'Smarties', 'Buttons', 'Milky Way',
          'Aero', 'Wispa', 'Crunchie', 'Double Decker', 'Boost', 'Picnic',
          'Yorkie', 'Lion Bar', 'Toffee Crisp', 'Caramel', 'Fudge',
          'Roses', 'Quality Street', 'Celebrations', 'Heroes', 'Miniatures',
          'After Eights', 'Ferrero Rocher', 'Kinder', 'Terry\'s Chocolate Orange'
        ]
      },
      {
        id: 'sweets',
        name: 'Sweets & Candy',
        items: [
          'Sweets', 'Candy', 'Haribo', 'Starmix', 'Tangfastics', 'Wine Gums',
          'Fruit Pastilles', 'Fruit Gums', 'Jelly Babies', 'Jelly Beans',
          'Liquorice', 'Liquorice Allsorts', 'Dolly Mixtures',
          'Mints', 'Polo Mints', 'Extra Strong Mints', 'Tic Tacs', 'Mentos',
          'Chewing Gum', 'Wrigleys', 'Extra', 'Airwaves', 'Orbit',
          'Lollipops', 'Chupa Chups', 'Drumsticks', 'Refreshers',
          'Fudge', 'Toffees', 'Werther\'s', 'Fisherman\'s Friend',
          'Fruit Chews', 'Starburst', 'Chewits', 'Fruitella', 'Skittles'
        ]
      },
      {
        id: 'biscuits-cakes',
        name: 'Biscuits & Cakes',
        items: [
          'Biscuits', 'Cookies', 'Digestives', 'Rich Tea', 'Hobnobs', 'Bourbons',
          'Custard Creams', 'Jammy Dodgers', 'Nice Biscuits', 'Malted Milk',
          'Shortbread', 'Ginger Nuts', 'Fig Rolls', 'Jaffa Cakes',
          'Chocolate Digestives', 'Chocolate Hobnobs', 'Maryland Cookies', 'Oreos',
          'McVities', 'Foxs', 'Crawfords',
          'Cake', 'Madeira Cake', 'Lemon Drizzle', 'Victoria Sponge', 'Fruit Cake',
          'Battenberg', 'Angel Cake', 'Swiss Roll', 'Mr Kipling', 'French Fancies',
          'Muffins', 'Chocolate Muffins', 'Blueberry Muffins', 'Cupcakes',
          'Doughnuts', 'Donuts', 'Krispy Kreme', 'Flapjacks', 'Brownies',
          'Croissants', 'Pain au Chocolat', 'Scones'
        ]
      }
    ]
  },

  // ══════════════════════════════════════════════════════════════════════════
  // 9. BABY & CHILDCARE
  // ══════════════════════════════════════════════════════════════════════════
  'baby-childcare': {
    id: 'baby-childcare',
    name: 'Baby & Childcare',
    emoji: '👶',
    subcategories: [
      {
        id: 'nappies-wipes',
        name: 'Nappies & Wipes',
        items: [
          'Nappies', 'Diapers', 'Pampers', 'Huggies', 'Aldi Mamia', 'Lidl Lupilu',
          'Pull-Ups', 'Training Pants', 'Swim Nappies',
          'Baby Wipes', 'Pampers Wipes', 'Water Wipes', 'Sensitive Wipes',
          'Nappy Sacks', 'Nappy Bags', 'Nappy Cream', 'Sudocrem', 'Bepanthen',
          'Changing Mat', 'Cotton Wool'
        ]
      },
      {
        id: 'baby-food',
        name: 'Baby Food & Formula',
        items: [
          'Baby Formula', 'Formula Milk', 'Aptamil', 'SMA', 'Cow and Gate', 'Hipp',
          'Follow On Milk', 'Growing Up Milk', 'Ready Made Formula',
          'Baby Food', 'Baby Puree', 'Ella\'s Kitchen', 'Heinz Baby', 'Cow and Gate Jars',
          'Baby Rice', 'Baby Cereal', 'Baby Porridge', 'Baby Snacks',
          'Organix', 'Baby Rusks', 'Finger Food',
          'Baby Bottles', 'Tommee Tippee', 'Avent', 'MAM', 'Dr Brown',
          'Bottle Teats', 'Steriliser Tablets', 'Bottle Brush'
        ]
      },
      {
        id: 'baby-care',
        name: 'Baby Care',
        items: [
          'Baby Shampoo', 'Baby Wash', 'Johnson\'s Baby', 'Childs Farm',
          'Baby Lotion', 'Baby Oil', 'Baby Powder',
          'Teething Gel', 'Bonjela', 'Calpol', 'Infant Paracetamol',
          'Baby Thermometer', 'Nasal Aspirator', 'Gripe Water', 'Infacol',
          'Dummies', 'Pacifiers', 'Soothers', 'Dummy Clips',
          'Bibs', 'Muslins', 'Baby Towels'
        ]
      }
    ]
  },

  // ══════════════════════════════════════════════════════════════════════════
  // 10. PET SUPPLIES
  // ══════════════════════════════════════════════════════════════════════════
  'pet-supplies': {
    id: 'pet-supplies',
    name: 'Pet Supplies',
    emoji: '🐾',
    subcategories: [
      {
        id: 'dog-food',
        name: 'Dog Food & Treats',
        items: [
          'Dog Food', 'Dry Dog Food', 'Wet Dog Food', 'Pedigree', 'Bakers', 'Harringtons',
          'Butchers', 'Cesar', 'Lily\'s Kitchen', 'James Wellbeloved',
          'Puppy Food', 'Senior Dog Food', 'Dog Treats', 'Dentastix', 'Gravy Bones',
          'Dog Biscuits', 'Dog Chews', 'Rawhide', 'Pigs Ears'
        ]
      },
      {
        id: 'cat-food',
        name: 'Cat Food & Treats',
        items: [
          'Cat Food', 'Dry Cat Food', 'Wet Cat Food', 'Whiskas', 'Felix', 'Sheba',
          'Gourmet', 'Iams', 'Royal Canin', 'Purina One',
          'Kitten Food', 'Senior Cat Food', 'Cat Treats', 'Dreamies', 'Cat Biscuits',
          'Cat Milk'
        ]
      },
      {
        id: 'pet-care',
        name: 'Pet Care',
        items: [
          'Cat Litter', 'Clumping Litter', 'Cat Litter Tray', 'Litter Liners',
          'Dog Shampoo', 'Cat Shampoo', 'Pet Shampoo', 'Flea Treatment',
          'Frontline', 'Advantage', 'Worming Tablets', 'Dog Wormer', 'Cat Wormer',
          'Dog Collar', 'Dog Lead', 'Cat Collar', 'Pet Bed', 'Dog Bed',
          'Dog Toys', 'Cat Toys', 'Pet Bowl', 'Water Bowl', 'Food Bowl',
          'Poo Bags', 'Dog Poo Bags', 'Puppy Pads', 'Training Pads'
        ]
      },
      {
        id: 'other-pets',
        name: 'Other Pet Food',
        items: [
          'Fish Food', 'Goldfish Food', 'Tropical Fish Food',
          'Bird Food', 'Bird Seed', 'Budgie Food', 'Parrot Food',
          'Rabbit Food', 'Guinea Pig Food', 'Hamster Food', 'Hay', 'Straw',
          'Small Animal Bedding', 'Wood Shavings'
        ]
      }
    ]
  }
};

/**
 * Get all catalogue categories as an array for UI rendering
 */
export function getCatalogueCategories() {
  return Object.values(SHOPPING_CATALOGUE);
}

/**
 * Find a category by ID
 */
export function findCatalogueCategory(categoryId) {
  return SHOPPING_CATALOGUE[categoryId] || null;
}

/**
 * Find a subcategory by category and subcategory ID
 */
export function findSubcategory(categoryId, subcategoryId) {
  const category = SHOPPING_CATALOGUE[categoryId];
  if (!category) return null;
  return category.subcategories.find(sub => sub.id === subcategoryId) || null;
}

/**
 * Search all items across categories
 */
export function searchCatalogueItems(query) {
  const results = [];
  const lowerQuery = query.toLowerCase();
  
  Object.values(SHOPPING_CATALOGUE).forEach(category => {
    category.subcategories.forEach(subcategory => {
      subcategory.items.forEach(item => {
        if (item.toLowerCase().includes(lowerQuery)) {
          results.push({
            name: item,
            category: category.name,
            categoryId: category.id,
            subcategory: subcategory.name,
            subcategoryId: subcategory.id
          });
        }
      });
    });
  });
  
  return results;
}

/**
 * Get all items as a flat list (for autocomplete)
 */
export function getAllCatalogueItems() {
  const items = [];
  
  Object.values(SHOPPING_CATALOGUE).forEach(category => {
    category.subcategories.forEach(subcategory => {
      subcategory.items.forEach(item => {
        items.push({
          name: item,
          category: category.name,
          categoryId: category.id,
          subcategory: subcategory.name,
          subcategoryId: subcategory.id
        });
      });
    });
  });
  
  return items;
}

export default SHOPPING_CATALOGUE;
